import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "../../../lib/supabaseClient";
import type { ConversationMessage, ConversationThreadView } from "../../../store/types";
import CustomerLayout from "../components/CustomerLayout";
import { useCustomerAuth } from "../../../contexts/CustomerAuthContext";

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString("zh-TW", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

interface Itinerary { meeting_point: string; meeting_time: string | null; map_url: string; notes: string; }

/** Detect if a message is a dispatch-originated system notice */
function isDispatchNotice(msg: ConversationMessage): boolean {
  return (
    msg.message_type === "system_notice" &&
    (msg.sender_type === "system" ||
      (msg.sender_type === "admin" && msg.sender_id === "system"))
  );
}

export default function CustomerMessagesPage() {
  const { session } = useCustomerAuth();
  const [threads, setThreads] = useState<ConversationThreadView[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [loadingThreads, setLoadingThreads] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [itinerary, setItinerary] = useState<Itinerary | null>(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [dismissedNotice, setDismissedNotice] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const selectedThread = threads.find((t) => t.id === selectedId) ?? null;

  const fetchThreads = useCallback(async () => {
    if (!supabase || !session?.order_id) return;
    setLoadingThreads(true);
    const { data } = await supabase
      .from("conversation_threads")
      .select("*")
      .eq("order_id", session.order_id)
      .eq("participant_type", "customer")
      .order("updated_at", { ascending: false });
    if (data) {
      setThreads((data as ConversationThreadView[]).map((row) => ({
        ...row,
        order_number: session.order_number,
        customer_name: session.customer_name,
      })));
    }
    setLoadingThreads(false);
  }, [session]);

  const fetchMessages = useCallback(async (threadId: string) => {
    if (!supabase) return;
    setLoadingMessages(true);
    const { data } = await supabase.from("conversation_messages").select("*").eq("thread_id", threadId).order("created_at", { ascending: true });
    if (data) setMessages(data as ConversationMessage[]);
    await supabase.from("conversation_messages").update({ is_read_customer: true }).eq("thread_id", threadId).eq("is_read_customer", false);
    setLoadingMessages(false);
  }, []);

  const fetchItinerary = useCallback(async () => {
    if (!supabase || !session?.order_id) return;
    const { data } = await supabase.from("itineraries").select("*").eq("order_id", session.order_id).maybeSingle();
    setItinerary(data as Itinerary | null);
  }, [session]);

  useEffect(() => { fetchThreads(); fetchItinerary(); }, [fetchThreads, fetchItinerary]);

  useEffect(() => {
    if (!selectedId) { setMessages([]); return; }
    fetchMessages(selectedId);
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => fetchMessages(selectedId), 8000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [selectedId, fetchMessages]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || !selectedId || sending) return;
    setSending(true);
    await supabase?.from("conversation_messages").insert([{
      thread_id: selectedId, sender_type: "customer",
      sender_id: session?.order_id ?? "customer",
      body: trimmed, message_type: "text", is_read_customer: true,
    }]);
    await supabase?.from("conversation_threads").update({ updated_at: new Date().toISOString() }).eq("id", selectedId);
    setInput("");
    setSending(false);
    await fetchMessages(selectedId);
  };

  const SENDER_COLOR: Record<string, string> = {
    admin: "bg-[#2C2C2C] text-white",
    photographer: "bg-amber-100 text-amber-800",
    customer: "bg-emerald-100 text-emerald-800",
    system: "bg-teal-100 text-teal-700",
  };
  const SENDER_LABEL: Record<string, string> = {
    admin: "平台運營",
    photographer: "攝影師服務",
    customer: "我",
    system: "平台通知",
  };

  // Latest dispatch notice to surface as callout
  const latestDispatchNotice = messages.findLast?.(isDispatchNotice) ?? messages.filter(isDispatchNotice).at(-1);
  const showDispatchCallout = latestDispatchNotice && dismissedNotice !== latestDispatchNotice.id;

  return (
    <CustomerLayout title="">
      {/* Platform safety warning */}
      <div className="bg-[#FFF8F0] border border-[#F5DFB8] rounded-lg px-4 py-3 mb-4 flex items-start gap-3">
        <div className="w-4 h-4 flex items-center justify-center text-[#C8963C] mt-0.5 shrink-0"><i className="ri-shield-line text-sm" /></div>
        <div className="text-xs font-light text-[#7A5C2C] leading-relaxed">
          <strong className="font-medium">安全提醒：</strong>
          請勿直接付款給攝影師 · 如需更改或加購，請透過此平台申請 · 請勿在平台外與攝影師聯繫
        </div>
      </div>

      {/* Dispatch-created notice callout — prominent "schedule arranged" banner */}
      {showDispatchCallout && (
        <div className="mb-4 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-100 text-emerald-600 shrink-0">
                <i className="ri-calendar-check-2-line text-base" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm font-medium text-emerald-800">攝影服務已排班</p>
                  <span className="px-1.5 py-0.5 rounded bg-emerald-200 text-emerald-700 text-[9px] font-medium whitespace-nowrap">平台通知</span>
                </div>
                <p className="text-xs text-emerald-700/80 font-light leading-relaxed mb-2">{latestDispatchNotice.body}</p>
                <div className="flex flex-wrap gap-2">
                  {itinerary?.meeting_point && (
                    <span className="px-2 py-0.5 rounded-full bg-white border border-emerald-200 text-emerald-700 text-[10px] font-light">
                      <i className="ri-map-pin-line mr-1 text-[9px]" />集合地點：{itinerary.meeting_point}
                    </span>
                  )}
                  {itinerary?.meeting_time && (
                    <span className="px-2 py-0.5 rounded-full bg-white border border-emerald-200 text-emerald-700 text-[10px] font-light">
                      <i className="ri-time-line mr-1 text-[9px]" />
                      {new Date(itinerary.meeting_time).toLocaleString("zh-TW", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </span>
                  )}
                  {!itinerary?.meeting_point && (
                    <span className="px-2 py-0.5 rounded-full bg-white border border-emerald-200 text-emerald-700 text-[10px] font-light italic">
                      集合資訊確認後將通知您
                    </span>
                  )}
                </div>
                <p className="text-[10px] text-emerald-600/60 font-light mt-2">如有任何疑問，請透過下方訊息聯繫平台運營</p>
              </div>
            </div>
            <button
              onClick={() => setDismissedNotice(latestDispatchNotice.id)}
              className="w-5 h-5 flex items-center justify-center text-emerald-400 hover:text-emerald-600 transition-colors cursor-pointer shrink-0"
            >
              <i className="ri-close-line text-sm" />
            </button>
          </div>
        </div>
      )}

      <div className="flex gap-4 h-[calc(100vh-300px)] min-h-96">
        {/* Thread list */}
        <div className="w-56 shrink-0 flex flex-col bg-white rounded-xl border border-[#E8E0D5] overflow-hidden">
          <div className="px-4 py-3 border-b border-[#F0EAE2]"><h3 className="text-xs font-medium text-[#2C2C2C] tracking-wide">訊息</h3></div>
          <div className="flex-1 overflow-y-auto">
            {loadingThreads ? (
              <div className="flex justify-center py-8"><p className="text-[11px] text-[#2C2C2C]/30">載入中...</p></div>
            ) : threads.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
                <div className="w-7 h-7 flex items-center justify-center text-[#2C2C2C]/15 mb-2"><i className="ri-message-3-line text-xl" /></div>
                <p className="text-[11px] text-[#2C2C2C]/30 font-light">等待排班通知</p>
                <p className="text-[10px] text-[#2C2C2C]/20 font-light mt-1">確認後平台將為您開通訊息</p>
              </div>
            ) : threads.map((t) => (
              <button key={t.id} onClick={() => setSelectedId(t.id)}
                className={`w-full text-left px-4 py-3 border-b border-[#F7F6F3] transition-colors cursor-pointer ${selectedId === t.id ? "bg-[#F7F6F3]" : "hover:bg-[#FAFAF8]"}`}>
                <p className="text-xs font-medium text-[#2C2C2C] truncate mb-1">{t.subject}</p>
                <p className="text-[10px] text-[#2C2C2C]/40">{new Date(t.updated_at).toLocaleDateString("zh-TW")}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Message panel */}
        <div className="flex-1 min-w-0 flex flex-col bg-white rounded-xl border border-[#E8E0D5] overflow-hidden">
          {!selectedThread ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
              {threads.length === 0 ? (
                <>
                  <div className="w-10 h-10 flex items-center justify-center text-[#2C2C2C]/15 mb-3"><i className="ri-time-line text-3xl" /></div>
                  <p className="text-sm text-[#2C2C2C]/40 font-light">等待排班中</p>
                  <p className="text-xs text-[#2C2C2C]/25 font-light mt-1">攝影師確認後，平台將為您發送服務資訊</p>
                </>
              ) : (
                <>
                  <div className="w-9 h-9 flex items-center justify-center text-[#2C2C2C]/15 mb-2"><i className="ri-chat-3-line text-3xl" /></div>
                  <p className="text-sm text-[#2C2C2C]/30 font-light">選擇左側訊息</p>
                </>
              )}
            </div>
          ) : (
            <>
              {/* Info header */}
              <div className="px-4 py-3 border-b border-[#F0EAE2]">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-[#2C2C2C]">{selectedThread.subject}</p>
                  <span className={`text-[10px] px-2 py-0.5 rounded font-light whitespace-nowrap ${selectedThread.status === "open" ? "bg-emerald-100 text-emerald-700" : "bg-[#F0EAE2] text-[#8B7355]"}`}>
                    {selectedThread.status === "open" ? "進行中" : "已關閉"}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-[#F7F6F3] rounded-lg px-3 py-2">
                    <p className="text-[9px] text-[#2C2C2C]/35 mb-0.5">訂單</p>
                    <p className="text-[10px] text-[#2C2C2C]/70">{session?.order_number}</p>
                  </div>
                  {itinerary?.meeting_point ? (
                    <div className="bg-[#F7F6F3] rounded-lg px-3 py-2">
                      <p className="text-[9px] text-[#2C2C2C]/35 mb-0.5">集合地點</p>
                      <p className="text-[10px] text-[#2C2C2C]/70 truncate">{itinerary.meeting_point}</p>
                    </div>
                  ) : (
                    <div className="bg-[#F7F6F3] rounded-lg px-3 py-2">
                      <p className="text-[9px] text-[#2C2C2C]/35 mb-0.5">集合地點</p>
                      <p className="text-[10px] text-[#2C2C2C]/40 italic font-light">確認後通知</p>
                    </div>
                  )}
                  {itinerary?.meeting_time && (
                    <div className="bg-[#F7F6F3] rounded-lg px-3 py-2">
                      <p className="text-[9px] text-[#2C2C2C]/35 mb-0.5">集合時間</p>
                      <p className="text-[10px] text-[#2C2C2C]/70">
                        {new Date(itinerary.meeting_time).toLocaleString("zh-TW", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>
                  )}
                  {itinerary?.map_url && (
                    <div className="bg-[#F7F6F3] rounded-lg px-3 py-2">
                      <p className="text-[9px] text-[#2C2C2C]/35 mb-0.5">地圖</p>
                      <a href={itinerary.map_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#C8B8A6] underline cursor-pointer">查看地圖</a>
                    </div>
                  )}
                </div>
                <div className="mt-2 flex items-center gap-1.5 px-3 py-1.5 bg-[#F0EAE2] rounded-lg">
                  <div className="w-3.5 h-3.5 flex items-center justify-center text-[#C8B8A6] shrink-0"><i className="ri-camera-2-line text-xs" /></div>
                  <p className="text-[10px] text-[#8B7355] font-light">攝影師聯絡 — 透過平台進行</p>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                {loadingMessages ? (
                  <div className="flex justify-center py-6"><p className="text-xs text-[#2C2C2C]/30">載入中...</p></div>
                ) : messages.length === 0 ? (
                  <div className="flex justify-center py-6"><p className="text-xs text-[#2C2C2C]/25 font-light">尚無訊息</p></div>
                ) : messages.map((msg) => {
                  const isMe = msg.sender_type === "customer";
                  const hasBlock = msg.is_blocked;
                  const isDispatch = isDispatchNotice(msg);
                  return (
                    <div key={msg.id} className={`flex flex-col gap-1 ${isMe ? "items-end" : "items-start"}`}>
                      <div className="flex items-center gap-1.5">
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-light ${SENDER_COLOR[msg.sender_type] || "bg-gray-100 text-gray-500"}`}>
                          {isDispatch ? "平台通知" : (SENDER_LABEL[msg.sender_type] || msg.sender_type)}
                        </span>
                        <span className="text-[9px] text-[#2C2C2C]/25">{formatTime(msg.created_at)}</span>
                        {hasBlock && <span className="text-[9px] text-red-400">⊘ 已封鎖</span>}
                      </div>
                      <div className={`max-w-xs px-3 py-2 rounded-lg text-xs font-light leading-relaxed ${
                        hasBlock ? "bg-red-50 border border-red-200 text-red-400 line-through" :
                        isDispatch ? "bg-emerald-50 border border-emerald-200 text-emerald-800 italic" :
                        msg.message_type === "system_notice" ? "bg-[#F0EAE2] border border-[#D9C9B4] text-[#8B7355] italic" :
                        isMe ? "bg-emerald-50 border border-emerald-100 text-[#2C2C2C]" : "bg-[#2C2C2C] text-white"
                      }`}>{msg.body}</div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>

              {/* Input */}
              <div className="border-t border-[#F0EAE2] px-4 py-3 flex gap-2">
                <textarea value={input} onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleSend(); }}
                  placeholder="傳送訊息給運營團隊... (Ctrl+Enter)" rows={2}
                  disabled={selectedThread.status === "closed"}
                  className="flex-1 px-3 py-2 text-xs font-light border border-[#E8E0D5] rounded-lg bg-[#F7F6F3] text-[#2C2C2C] placeholder-[#2C2C2C]/30 focus:outline-none focus:border-[#C8B8A6] resize-none disabled:opacity-40 text-sm" />
                <button onClick={handleSend} disabled={!input.trim() || sending || selectedThread.status === "closed"}
                  className="self-end w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-600 text-white disabled:opacity-40 hover:bg-emerald-700 transition-colors cursor-pointer shrink-0">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-send-plane-fill text-sm" /></div>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </CustomerLayout>
  );
}
